import { useTranslation } from 'react-i18next';

import { Image, StyleSheet, Text, View } from '@react-pdf/renderer';
import { SmallHeading } from 'PDF/components/SmallHeading';
import { getColorObjectsStyleByRecord } from 'PDF/helpers/documentStyle';
import { formatDateForPDF } from 'PDF/helpers/formatDates';

import { IKeyPersonnelProfilesTableItem } from './types';
import { WorkInfoRow } from './WorkInfoRow';

interface IKeyPersonnelProfilesTableProps {
  items: IKeyPersonnelProfilesTableItem[];
  isExamplePage: boolean
}

const colorIndexes = {
  green: [12, 32],
  blue: [5, 2],
  red: [5, 2],
  blueGreen: [0, 2],
  blueRed: [1, 2],
  blueYellow: [5, 2],
};

export const KeyPersonnelProfilesTable = ({ items, isExamplePage }: IKeyPersonnelProfilesTableProps) => {
  const {t} = useTranslation()
  const [nameColor, personnelInfoColor] = getColorObjectsStyleByRecord(colorIndexes, ['color', 'backgroundColor']);
  const nameStyle = [styles.infoHeading, nameColor];

  return (
    <>
      <View minPresenceAhead={300} />
      <View style={{ marginBottom: 30 }}>
        <SmallHeading heading={isExamplePage ? "Key Personnel Profiles" : t('pdfPages.mappings.managementAndOrganization.keyPersonProfile')} />
        {items.map(({ name, surname, dateOfBirth, photo, rows }, index) => (
          <>
            {index !== 0 && <View minPresenceAhead={180} />}
            <View key={index} style={[styles.wrap, { marginTop: index === 0 ? 0 : 40 }]}>
              <View style={styles.photoColumnWrap}>
                {photo && (
                  <View>
                    <Image src={photo} style={styles.img} />
                  </View>
                )}
                <View style={[styles.personnelInfoRow, personnelInfoColor]}>
                  <View style={styles.firstCeil}>
                    <View style={[styles.textWrap, { flexDirection: 'row', gap: '20px' }]}>
                      <Text style={[styles.infoHeading, { width: '75px' }]}>{isExamplePage ? 'Name' : t('management.team.name')}</Text>
                      <Text style={[...nameStyle, { width: '100%' }]}>{name}</Text>
                    </View>
                    <View style={[styles.textWrap, { flexDirection: 'row', gap: '20px' }]}>
                      <Text style={[styles.infoHeading, { width: '75px' }]}>{isExamplePage ? 'Surname' : t('management.team.surname')}</Text>

                      <Text style={[...nameStyle, { width: '100%' }]}>{surname}</Text>
                    </View>
                  </View>
                </View>
                {dateOfBirth && (
                  <View style={[styles.personnelInfoRow, personnelInfoColor, { padding: '0px 13px 5px 6px' }]}>
                    <View style={styles.ceil}>
                      <View style={styles.textWrap}>
                        <Text style={styles.infoHeading}>{isExamplePage ? 'Date of birth' : t('management.team.dateOfBirth')}</Text>
                        <Text style={[styles.infoHeading, styles.text]}>
                          {formatDateForPDF(dateOfBirth, 'MM.DD.YYYY', false)}
                        </Text>
                      </View>
                    </View>
                  </View>
                )}

                <View fixed style={{ width: '159px' }}></View>
              </View>
              <View style={styles.workInfoColumnWrap}>
                {rows.map((item, index) => (
                  <WorkInfoRow key={index} heading={item.heading} info={item.info} isLast={index === rows.length - 1} />
                ))}
              </View>
            </View>
          </>
        ))}
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  wrap: {
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
  },
  photoColumnWrap: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  img: {
    width: '159.09px',
    height: '176.3px',
    marginBottom: '7.22px',
    objectFit: 'cover',
  },
  personnelInfoRow: {
    display: 'flex',
    flexDirection: 'row',
    width: '157px',
    minHeight: '30px',
    padding: '5px 13px 0 6px',
  },
  textWrap: {
    display: 'flex',
    flexDirection: 'column',
    padding: '4px 0',
    width: '100%',
    overflow: 'hidden',
  },
  infoHeading: {
    fontFamily: 'Noto',
    fontSize: '9px',
    fontWeight: 600,
    color: '#000',
  },
  text: {
    fontWeight: 400,
  },
  firstCeil: {
    borderTop: '0.5px solid #fff',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  ceil: {
    borderBottom: '0.5px solid #fff',
    borderTop: '0.5px solid #fff',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  workInfoColumnWrap: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '15px',
  },
});
